import * as yup from "yup";
import type { GetQueryProps } from "./common";

export interface OperatingHours {
  day: string;
  start_time: string;
  end_time: string;
}
export interface UpdateMspInput {
  phone_number?: string;
  company_name?: string;
  brand_name?: string;
  data_center?: string;
  country?: number;
  state?: number;
  city?: string;
  postal_code?: string;
  website?: string;
  address1?: string;
  address2?: string;
  time_zone?: string;
  date_format?: string;
  operating_hours_from?: string;
  operating_hours_to?: string;
  latitude?: string;
  longitude?: string;
  billing_name?: string;
  billing_building_name?: string;
  billing_floor?: string;
  billing_country?: number;
  billing_state?: number;
  billing_city?: string;
  billing_postal_code?: string;
  default_currency?: string;
  mspOperatingHours?: Array<OperatingHours>;
  is_24x7?: boolean;
  business_hours?: string;
}

export interface UpdateMspCustomNameInput {
  custom_name?: string;
}

export interface BulkAddResourcesInput {
  userData?: { email?: string; resource_type?: string; name?: string }[];
}

export interface UpdateResourceInput {
  resourceId: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  phone_number?: string;
  office_desk_number?: string;
  office_location?: string;
  resource_work_type?: string;
  resource_work_role?: string;
  department?: string;
  is_locked?: boolean;
  manager?: string;
  /** Role / resource-type id assigned to the member. */
  resource_type?: string;
  /** Whether AI ticket routing is enabled for this member. */
  ai_routing?: boolean;
}

export interface UpdateResourceActiveInput {
  resourceId: string;
  isActive: boolean;
}

export interface UpdateResourceLockInput {
  resourceId: string;
  isLocked: boolean;
}

export interface CreateResourceInput {
  email: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  display_name?: string;
  resource_type: string;
  location?: string;
  resource_work_type?: string;
  time_zone: string;
}

export interface FetchResourcesByPermissionsInput extends GetQueryProps {
  customer_id?: string;
  serviceType?: "tickets" | "tasks" | "projects";
}

export interface UploadProfilePictureInput {
  resourceId: string;
  formData: FormData;
}

export interface UpdateProfileDetailsInput {
  department: string;
  first_name: string;
  last_name: string;
  location: string;
  middle_name: string;
  office_desk_number: string;
  phone_number: string;
  resource_work_role: string;
  resource_work_type: string;
}

export interface ResourceResendInviteInput {
  email: string;
  tenantId: string;
}

export interface UpdateMspNotificationInputOld {
  notificationType: string;
  notificationStatus: boolean;
  notificationSound: boolean;
  notifyMethods: Array<string>;
  notifyRoles: Array<Record<string, string | null>>;
}

export interface UpdateMspNotificationInput {
  data: {
    notification_status: boolean;
    notification_sound: boolean;
    notify_methods: unknown[];
    /**
     * Ticket-status `mapping_id`s; `[]` / `null` = every status; key omitted =
     * stored selection kept. Accepted only on `ticket_status_update` (MSP) and
     * `update_ticket` (customer) — the backend rejects it on any other notify
     * type, so omit it there.
     */
    status_ids?: string[] | null;
  };
  notificationType: string;
}

export interface MspNotificationRole {
  role_id: string;
  role_name: string;
}

export interface MspNotificationMethod {
  notify_through: string;
  roles: MspNotificationRole[];
}

/** One entry of `GET v2/controlCenter/msp_notifications` → `data.result[]`. */
export interface MspNotificationEntry {
  msp_id: string;
  mapping_id: string;
  notify_type: string;
  notification_status: boolean;
  notification_sound: boolean;
  /**
   * Always an array; `[]` = every status. Meaningful on `ticket_status_update`
   * (MSP) and `update_ticket` (customer) only.
   */
  status_ids: string[];
  /** Singular on GET; the PATCH body takes `notify_methods`. */
  notify_method: MspNotificationMethod[];
}

export interface UpdateMspBillingInput {
  mspId: string;
  data: {
    billingName?: string;
    billingAddressLine1?: string;
    billingAddressLine2?: string;
    billingCountry?: number | null;
    billingState?: number | null;
    billingCity?: string | null;
    billingZipCode?: string | null;
  };
}

export interface CreateLocationInput {
  location_name: string;
  contact_person?: string | null;
  location_description?: string | null;
  email: string;
  contact_number?: string | null;
  country: number;
  state?: number | null;
  city?: string | null;
  street?: string | null;
  postal_code: string;
  timezone: string;
}

export interface UpdateLocationInput extends CreateLocationInput {
  locationId: string;
}

const LocationResultSchema = yup.object().shape({
  mapping_id: yup.string().required(),
  location_name: yup.string().required(),
  contact_person: yup.string().nullable(),
  location_description: yup.string().nullable(),
  email: yup.string().email().required(),
  contact_number: yup.string().nullable(),
  country: yup.number().nullable(),
  state: yup.number().nullable(),
  city: yup.string().nullable(),
  street: yup.string().nullable(),
  postal_code: yup.string().nullable(),
  timezone: yup.string().nullable(),
  meta: yup
    .object()
    .shape({
      is_default: yup.boolean().nullable(),
    })
    .nullable(),
});

export const GetAllLocationsResponseSchema = yup.object().shape({
  totalCount: yup.string().required(),
  totalPages: yup.number().nullable(),
  result: yup.array(LocationResultSchema),
});

export type LocationResultType = yup.InferType<typeof LocationResultSchema>;
export type getAllLocationsResponseType = yup.InferType<
  typeof GetAllLocationsResponseSchema
>;

export interface CreateResourceTypeRolesPermissionsInput {
  resource_type_name: string;
  resource_type_description: string;
  permissions: Array<string>;
  customers?: Array<unknown>;
}

export interface FetchResourceAndPermissionByIdInput {
  resource_type_id?: string;
}

export interface UpdateResourceRoleMembersInput {
  role_id: string;
  resource_id: Array<string>;
}

interface CustomerPermissionInput {
  customer_id: string;
  permission: "rw" | "r";
}

export interface UpdateResourceTypeRolesPermissionsInput {
  resource_type_id: string;
  resource_type_name?: string;
  resource_type_description?: string;
  permissions?: Array<string>;
  customers?: Array<CustomerPermissionInput>;
}
export interface CallBackOAuthInput {
  mappingId: string;
  p_id?: string;
  provider?: string;
  account_type?: string;
  /** "billing" tags the mail server for billing sends. Free text, no enum. */
  email_type?: string;
}

export interface UpdateEmailConfigurationInput {
  mappingId: string;
  display_from?: string;
  custom_display_name?: string;
  support_email?: string;
  authenticated_email?: string;
  board?: string;
  assignees?: Array<string>;
  /** "billing" promotes this mailbox; the endpoint never demotes. */
  email_type?: string;
  /** Only stored alongside `email_type: "billing"`. */
  reply_to_email?: string;
  /**
   * On a billing-tagged mailbox this is the active-mailbox switch, not a
   * disconnect: `true` falls sending back to the primary mailbox, `false`
   * reactivates this one and deactivates whichever was active. The mailbox
   * stays connected and billing-tagged either way.
   */
  is_disabled?: boolean;
}

export interface GetAllResourceTypeInput extends GetQueryProps {
  mspId?: string | null;
}

export interface BulkInviteResource {
  email: string;
  full_name: string;
  resource_type: string;
}
export interface UpdateEmailSignatureInput {
  signature_logo?: string;
  position: string;
  width: number;
  height: number;
  signature_text?: string;
  signature_html?: string;
  is_size_synced: boolean;
}

export interface UploadBrandingLogoInput {
  file: File;
  type: "logo" | "wordmark";
}

export interface BrandingLogoResponse {
  mapping_id?: string;
  url?: string;
  original_name?: string;
  encoding?: string;
  file_type?: string;
  multer_name?: string;
  size?: number;
  etag?: string;
  key?: string;
  bucket?: string;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  is_deleted?: boolean;
  branding_type?: "logo" | "wordmark";
  custom_branding?: boolean;
}

interface EmailAuth {
  user: string;
  pass: string;
}

interface EmailServerConfig {
  auth: EmailAuth;
  host: string;
  port: number;
  secure: boolean;
}

export interface AddCustomEmailConfigurationInput {
  /** Support mailboxes only — a billing mailbox never creates tickets. */
  board_id?: string;
  email: string;
  imap: EmailServerConfig;
  smtp: EmailServerConfig;
  display_from?: string;
  custom_display_name?: string;
  /** "billing" marks this as the dedicated billing mailbox. */
  email_type?: string;
  /** Only stored alongside `email_type: "billing"`. */
  reply_to_email?: string;
}

export interface UpdateCustomEmailConfigurationInput
  extends AddCustomEmailConfigurationInput {
  mail_server_id: string;
}

export interface VerifyCustomEmailConfigurationInput
  extends Partial<
    Omit<AddCustomEmailConfigurationInput, "board_id" | "email">
  > {}
